import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PanelList } from '../../../shared/models/panel-list';
import { TrainingOccurrenceSessionDetailsDto } from '../../user-training-plan/models/training-occurrence-session-details';
import { MySpaceConstants } from '../../my-space-constants';
import { DatePipe } from '@angular/common';
import { UserTrainingPlanService } from '../../user-training-plan/services/user-training-plan.service';
import { CommonService } from '../../../shared/services/common.service';

@Component({
  selector: 'app-session-panel-list',
  templateUrl: './session-panel-list.component.html',
  styleUrls: ['./session-panel-list.component.scss']
})
export class SessionPanelListComponent implements OnInit {
  @Input() sessionIds: string[] = [];
  @Output() showSessionDetailsModal = new EventEmitter<boolean>();

  isSessionDetailsAvailable = false;
  sessionDetailsData: PanelList = { lists: [], filters: [], sort: [] };
  virtualMeetingURLText = MySpaceConstants.Virtual_MeetingURL_Text;
  showAttachmentsModal = false;
  isShowAllSelected = false;
  sessionAttachments: any;
  noData = false;
  showSessionAuditModal = false;
  showLoader = false;
  isSessionDetailsModelOpen = false;
  sessionDetails: TrainingOccurrenceSessionDetailsDto[] = [];

  constructor(
    private datePipe: DatePipe,
    private userTrainingPlanService: UserTrainingPlanService,
    private commonService: CommonService
  ) {}

  ngOnInit() {
    this.isSessionDetailsModelOpen = true;
    this.getSessionDetailsForTrainingPlanOccurrence();
  }

  getSessionDetailsForTrainingPlanOccurrence() {
    this.showLoader = true;
    this.userTrainingPlanService.getTrainingOccurrenceSessionDetails(this.sessionIds).subscribe({
      next: serviceData => {
        if (serviceData) {
          this.sessionDetailsData.lists = this.getPanelListDataWithPropertyForSessions(serviceData, MySpaceConstants.Trainers_Field_Constant);
          this.sessionDetails = this.sessionDetailsData.lists;
          this.noData = this.commonService.IsListEmpty(this.sessionDetailsData.lists);
          this.bindPanelListData();
        }
        this.isSessionDetailsAvailable = true;
        this.showLoader = false;
      },
      error: () => {
        this.showLoader = false;
        this.isSessionDetailsAvailable = false;
        this.noData = true;
      }
    });
  }

  bindPanelListData() {
    this.addFilters();
    this.addSorting();
    this.isSessionDetailsAvailable = true;
  }

  getPanelListDataWithPropertyForSessions(data: any, propertyName: string): any {
    return data?.map((item: any) => ({
      ...item,
      [`${propertyName}List`]: this.GetUsersList(item[propertyName])
    }));
  }

  private GetUsersList(propertyValue: any): string[] {
    if (Array.isArray(propertyValue)) {
      return propertyValue
        .filter(obj => obj?.assignedUserName)
        .map(obj => obj.assignedUserName);
    }
    if (propertyValue?.assignedUserName) {
      return [propertyValue.assignedUserName];
    }
    if (propertyValue != null) {
      return [String(propertyValue)];
    }
    return [];
  }

  addFilters(): void {
    this.sessionDetailsData.filters = [
      { filterDisplayName: "Trainers", filterFieldName: "trainersList" },
      { filterDisplayName: "Format", filterFieldName: "format" },
      { filterDisplayName: "Status", filterFieldName: "status" },
      { filterDisplayName: "Enrollment", filterFieldName: "enrollment" }
    ];
    this.sessionDetailsData.enableFilter = true;
  }

  addSorting(): void {
    this.sessionDetailsData.sort = [
      { sortFieldDisplayName: "Trainers", sortFieldName: "trainersList" },
      { sortFieldDisplayName: "Format", sortFieldName: "format" },
      { sortFieldDisplayName: "Status", sortFieldName: "status" },
      { sortFieldDisplayName: "Enrollment", sortFieldName: "enrollment" },
      { sortFieldDisplayName: "Start Date", sortFieldName: "startDateAndTime" },
      { sortFieldDisplayName: "End Date", sortFieldName: "endDateAndTime" }
    ];
    this.sessionDetailsData.enableSort = true;
  }

  OpenVirtualMeeting(virtualMeetingURL: string) {
    window.open(virtualMeetingURL, "_blank");
  }

  OpenSessionAttachmentModal(sessionId: any) {
    this.showAttachmentsModal = true;
    this.sessionAttachments = [];
    this.loadAttachments(sessionId);
  }

  closeAttachmentModal(isAttachmentModalOpen: boolean) {
    this.showAttachmentsModal = isAttachmentModalOpen;
  }

  calculateStatusColor(status: string): string {
    switch (status) {
      case MySpaceConstants.StatusName_Scheduled:
        return MySpaceConstants.StatusClassName_Scheduled;
      case MySpaceConstants.StatusName_Completed:
        return MySpaceConstants.StatusClassName_Completed;
      case MySpaceConstants.StatusName_Cancelled:
        return MySpaceConstants.StatusClassName_Cancelled;
      default:
        return '';
    }
  }

  calculateEnrollmentColor(enrollment: string): string {
    switch (enrollment) {
      case MySpaceConstants.FormatName_Open:
        return MySpaceConstants.FormatClassName_Open;
      case MySpaceConstants.FormatName_Closed:
        return MySpaceConstants.FormatClassName_Closed;
      default:
        return '';
    }
  }

  private loadAttachments(sessionId: any) {
    this.sessionAttachments = this.sessionDetails.find(session => session.sessionId === sessionId)?.attachments;
  }

  setDateTime(data: any): string {
    const startDateTime = this.updateDateAndTime(new Date(data.startDateAndTime));
    const endDateTime = this.updateDateAndTime(new Date(data.endDateAndTime));
    const [startDate] = startDateTime.split("T");
    const [endDate] = endDateTime.split("T");

    if (startDate === endDate) {
      return `${this.datePipe.transform(new Date(startDateTime), 'dd-MMM-yyyy')}, ${this.datePipe.transform(new Date(startDateTime), 'H:mm')} - ${this.datePipe.transform(new Date(endDateTime), 'H:mm')} (${data.timeFormat})`;
    }
    return `${this.datePipe.transform(new Date(startDateTime), 'dd-MMM-yyyy, H:mm')} - ${this.datePipe.transform(new Date(endDateTime), 'dd-MMM-yyyy, H:mm')} (${data.timeFormat})`;
  }

  updateDateAndTime(dateTime: Date): string {
    return this.formatDateTimeToISOString(dateTime);
  }

  formatDateTimeToISOString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  closeSessionDetailsModal = () => {
    this.isSessionDetailsModelOpen = false;
    this.showSessionDetailsModal.emit(false);
  }
}
